import React from 'react'
import { Navigate, Outlet, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Box, CircularProgress, Typography, Container, Button } from '@mui/material'
import { selectTenant } from '../store/slices/authSlice'
import { TenantProvider, useTenant } from './TenantContext'

/**
 * TenantRoute: validates the URL slug matches the authenticated user's tenant.
 * Wraps children in TenantProvider for access to tenant context.
 */
const TenantRouteInner = () => {
  const { slug } = useParams()
  const { isAuthenticated, loading: authLoading, bootstrapping } = useSelector((state) => state.auth)
  const tenantFromAuth = useSelector(selectTenant)
  const { loading: tenantLoading, error: tenantError } = useTenant()

  // Esperar también el bootstrap inicial de sesión (Supabase/Clerk). Sin esto,
  // TenantRoute (que corre antes que ProtectedRoute) expulsaba al login en el
  // refresh, antes de que hydrate cargara al usuario, pese a haber sesión válida.
  if (authLoading || bootstrapping) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="var(--cd-full-vh, 100vh)">
        <CircularProgress size={60} />
      </Box>
    )
  }

  // Sin sesión → cae en la VISTA PÚBLICA de la compañía (cara desconectada de
  // Cuartel Digital), no en el login. Así el QR puede apuntar a la raíz /:slug y
  // "lo que ves depende de si tenés sesión": anónimo = vista pública curada (sin
  // PII), miembro = app completa. La vista pública trae su propio "Iniciar sesión".
  // El portal es ruta hermana (fuera de TenantRoute) → no hay loop de redirección.
  if (!isAuthenticated) {
    return <Navigate to={`/${slug}/portal`} replace />
  }

  // If authenticated but slug doesn't match user's tenant, redirect to correct slug
  if (tenantFromAuth && tenantFromAuth.slug !== slug) {
    return <Navigate to={`/${tenantFromAuth.slug}/dashboard`} replace />
  }

  // If tenant info is loading
  if (tenantLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="var(--cd-full-vh, 100vh)">
        <CircularProgress size={60} />
      </Box>
    )
  }

  // If tenant not found
  if (tenantError) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Compañía no encontrada
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          La compañía &quot;{slug}&quot; no existe o no está activa.
        </Typography>
        <Button variant="contained" href="/">
          Volver al inicio
        </Button>
      </Container>
    )
  }

  return <Outlet />
}

const TenantRoute = () => {
  return (
    <TenantProvider>
      <TenantRouteInner />
    </TenantProvider>
  )
}

export default TenantRoute
